import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import LanguageToggle from "../components/LanguageToggle";
import { useSurveyCircle } from "../hooks/useSurveyCircle";

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isSurveyCircle } = useSurveyCircle();
  const [draft, setDraft] = useState("");
  const taRef = useRef(null);

  useEffect(() => {
    // Soft, delayed focus — invites without forcing
    const tid = setTimeout(() => taRef.current?.focus(), 600);
    return () => clearTimeout(tid);
  }, []);

  const handleStart = () => {
    const value = draft.trim();
    navigate("/flow", { state: value ? { initialContext: value } : undefined });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleStart();
    }
  };

  return (
    <div className="ob-shell" data-testid="landing-page">
      {/* Header */}
      <header
        className="flex items-center justify-between"
        style={{ padding: "1.4rem 2rem" }}
      >
        <span className="ob-logo" data-testid="brand-name">
          {t.brandName}
        </span>
        <LanguageToggle />
      </header>

      {/* Hero / Onboarding */}
      <main className="flex-1 flex items-center" style={{ padding: "0 1.5rem" }}>
        <div
          className="w-full mx-auto animate-rise"
          style={{ maxWidth: "640px" }}
        >
          <h1
            className="ob-question"
            data-testid="landing-headline"
          >
            {t.obQuestion || t.headline1}
          </h1>

          <p className="ob-sub" data-testid="landing-subhead">
            {t.obSub || t.subheadline}
          </p>

          <div className="ob-fieldwrap" data-testid="onboarding-field">
            <textarea
              ref={taRef}
              className="ob-field"
              data-testid="onboarding-input"
              placeholder={t.obPlaceholder || ""}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              maxLength={2000}
            />
          </div>

          {t.obExamples && (
            <p
              className="font-sans"
              data-testid="landing-examples"
              style={{
                marginTop: "0.75rem",
                fontSize: "0.78rem",
                color: "var(--ink-ghost)",
                lineHeight: 1.6,
                fontStyle: "italic",
                letterSpacing: "0.01em",
              }}
            >
              {t.obExamples}
            </p>
          )}

          <div
            className="flex items-center justify-between"
            style={{ marginTop: "1.5rem", flexWrap: "wrap", gap: "1rem" }}
          >
            <button
              data-testid="start-flow-btn"
              className="ob-cta"
              onClick={handleStart}
            >
              <span>{t.obCta || t.ctaButton}</span>
              <span className="arr">→</span>
            </button>

            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "var(--f-xs)",
                color: "var(--ink-3)",
                letterSpacing: "0.02em",
              }}
              data-testid="reassurance-line"
            >
              {t.obReassurance || t.trustLine}
            </span>
          </div>

          {isSurveyCircle && (
            <p
              data-testid="surveycircle-ps"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "var(--f-xs)",
                color: "var(--ink-4)",
                fontStyle: "normal",
                marginTop: "1.75rem",
                paddingLeft: "0.85rem",
                borderLeft: "1px solid var(--line)",
                maxWidth: "52ch",
                lineHeight: 1.65,
              }}
            >
              {t.surveyCirclePs}
            </p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="flex items-center justify-between"
        style={{ padding: "1.25rem 2rem" }}
      >
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "var(--f-xs)",
            color: "var(--ink-4)",
            letterSpacing: "0.02em",
          }}
        >
          {t.footer}
        </span>
        <span
          className="font-serif"
          style={{
            fontStyle: "italic",
            fontSize: "var(--f-xs)",
            color: "var(--ink-4)",
          }}
        >
          {t.footerNote || ""}
        </span>
      </footer>
    </div>
  );
};

export default LandingPage;
